import { useContext } from "react";
import { BlueBirdTheme, Typography } from "@kitbird/core";
import { StyledStep, StyledStepWrapper } from "./Stepper.styled";
import SvgDiv from "../SvgDiv";
import StepperContext from "./StepperContext";
import { ReactComponent as BaseLineIcon } from "../../assets/icons/baseline.svg";

type StepState = "passed" | "active" | "inactive";

interface StepProps extends React.ComponentPropsWithoutRef<"div"> {
  key?: number;
  currentIndex: number;
  stepIndex: number;
  dataTestId?: string;
  label?: string;
  name: string;

  /**
   * The icon to display for a step that is completed/passed.
   * @type JSX.Element
   */
  iconPassed: JSX.Element;

  /**
   * The icon to display for the currently active step.
   * @type JSX.Element
   */
  iconActive: JSX.Element;

  /**
   * The icon to display for a step that has not yet been reached.
   * @type JSX.Element
   */
  iconInactive: JSX.Element;
  disableClick?: boolean;
}

export default function Step({
  currentIndex,
  stepIndex,
  dataTestId,
  label,
  name,
  iconPassed,
  iconActive,
  iconInactive,
  disableClick = false,
  ...rest
}: StepProps) {
  const { stepLength, onClick } = useContext(StepperContext);

  const getState = (): StepState => {
    if (stepIndex < currentIndex) return "passed";
    if (stepIndex === currentIndex) return "active";
    return "inactive";
  };

  const state = getState();

  const selectIcon = () => {
    switch (state) {
      case "passed":
        return iconPassed;
      case "active":
        return iconActive;
      case "inactive":
      default:
        return iconInactive;
    }
  };

  const fontStyle: {
    weight: "bold" | "medium" | "regular";
    color: string;
  } = (() => {
    switch (state) {
      case "passed":
        return {
          weight: "medium",
          color: BlueBirdTheme.colors.gray.title,
        };
      case "active":
        return {
          weight: "regular",
          color: BlueBirdTheme.colors.gray.subtitle,
        };
      case "inactive":
      default:
        return {
          weight: "regular",
          color: BlueBirdTheme.colors.gray.disable,
        };
    }
  })();

  return (
    <StyledStep
      data-testid={dataTestId}
      onClick={() => {
        if (disableClick) return;
        onClick(name, stepIndex);
      }}
      {...rest}
    >
      <StyledStepWrapper>
        <SvgDiv>{selectIcon()}</SvgDiv>
        <Typography
          className="label-step"
          color={fontStyle.color}
          fontWeight={fontStyle.weight}
        >
          {label}
        </Typography>
      </StyledStepWrapper>
      {stepIndex < stepLength - 1 && (
        <SvgDiv $color={stepIndex >= currentIndex ? "#C4C4C4" : BlueBirdTheme.colors.primary[500]}>
          <BaseLineIcon />
        </SvgDiv>
      )}
    </StyledStep>
  );
}
